import DataController from '../../data/data.controller';
import { SSO_ENVIRONMENT } from './openSso.constants';

/**
 * Resolve the boot config for the embedded consumer app (rn-ui), impersonating
 * a user by clientId. Shared by the Open SSO route (`OpenSsoView`) and the
 * in-panel embed on the Reports page (`ReportsController.openSsoForUser`).
 *
 * ONE call: `POST /api/admin/users/:aba/:client_id/sso/session`. The server
 * checks `admin:sso:open` once, resolves the apiKey, the member's identity and
 * their service-resolved feature flags together, mints a `sessionId`, and writes
 * the `admin.sso.session_opened` audit row. It returns the whole bundle or it
 * fails — there is deliberately no partial result to fall back to.
 *
 * This replaced a three-call boot (apiKey, user context, member feature flags)
 * whose last two calls were best-effort: a 403 or 404 on the identity call still
 * produced a running consumer app booted with nothing but a clientId. Do not
 * reintroduce a try/catch around any of it — a session that cannot be fully
 * resolved must not open, and the caller shows the error instead.
 *
 * The SSN is still not sourced: the boot keeps a placeholder (see OpenSsoApp),
 * so `identity` carries only plaintext fields.
 *
 * @param {string} clientId
 * @returns {Promise<{ apiKey, environment, serviceUrl, clientId, institution, style, identity, featureFlags, sessionId }>}
 * @throws when the session is refused (403), the member has no stored SSO
 *   context (404 NO_SSO_CONTEXT), or the bundle comes back incomplete.
 */
export async function resolveSsoBoot(clientId) {
    const data = DataController.Instance();
    await data.authenticate();
    const aba = data.aba;
    if (!aba) throw new Error('No FI in session.');

    const fi = data.user?.fi || {};
    const session = await data.openSsoSession(aba, clientId);

    if (!session?.apiKey) throw new Error('No apiKey returned for this FI.');
    // The server refuses a member with no stored member number, so an identity
    // without one means the contract changed under us — better to stop than to
    // boot the degraded session this endpoint exists to prevent.
    if (!session.identity?.identifier) throw new Error('SSO session returned no member identity.');

    return {
        apiKey: session.apiKey,
        environment: SSO_ENVIRONMENT,
        serviceUrl: data.API.apiUrl,
        clientId,
        institution: { name: fi.name, aba, productName: fi.product_name },
        style: fi.style || {},
        identity: session.identity,
        featureFlags: session.featureFlags || {},
        sessionId: session.sessionId,
    };
}
